import React, { useState } from 'react';
import { BellIcon, AlertTriangleIcon, FileTextIcon, CheckCircleIcon, CalendarIcon, ArrowRightIcon, SettingsIcon, MailIcon, BellRingIcon } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Notification } from '../data/types';
export function Notifications() {
  const {
    state,
    dispatch,
    navigate
  } = useApp();
  const {
    notifications,
    language
  } = state;
  const [activeTab, setActiveTab] = useState<'all' | 'khajna' | 'mutation' | 'verification'>('all');
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({
    inAppKhajna: true,
    emailKhajna: true,
    inAppMutation: true,
    emailMutation: false,
    inAppVerification: true,
    emailVerification: false
  });
  const filteredNotifications = notifications.filter(n => {
    if (activeTab === 'all') return true;
    if (activeTab === 'khajna') return n.type === 'khajna-due';
    if (activeTab === 'mutation') return n.type === 'mutation-followup';
    if (activeTab === 'verification') return n.type === 'verification-changed';
    return true;
  });
  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'khajna-due':
        return <AlertTriangleIcon className="w-5 h-5 text-red-600" data-id="element-961" />;
      case 'mutation-followup':
        return <FileTextIcon className="w-5 h-5 text-purple-600" data-id="element-962" />;
      case 'verification-changed':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-963" />;
    }
  };
  const getUrgencyBadge = (urgency: Notification['urgency']) => {
    switch (urgency) {
      case 'high':
        return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium" data-id="element-964">
            Urgent
          </span>;
      case 'medium':
        return <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium" data-id="element-965">
            Soon
          </span>;
      case 'low':
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium" data-id="element-966">
            Info
          </span>;
    }
  };
  const handleOpenRecord = (notification: Notification) => {
    dispatch({
      type: 'MARK_NOTIFICATION_READ',
      payload: notification.id
    });
    const record = state.landRecords.find(r => r.landId === notification.landId);
    if (record) {
      dispatch({
        type: 'SET_SELECTED_RECORD',
        payload: record.id
      });
      navigate('land-record-details', record.id);
    }
  };
  const unreadCount = notifications.filter(n => !n.read).length;
  return <div className="p-4 lg:p-6 space-y-4" data-id="element-967">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-968">
        <div data-id="element-969">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-970">
            {language === 'en' ? 'Notifications' : 'বিজ্ঞপ্তি'}
          </h1>
          <p className="text-gray-600 mt-1" data-id="element-971">
            {unreadCount}{' '}
            {language === 'en' ? 'unread notifications' : 'অপঠিত বিজ্ঞপ্তি'}
          </p>
        </div>
        <button onClick={() => setShowSettings(!showSettings)} className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-972">
          <SettingsIcon className="w-5 h-5" data-id="element-973" />
          <span data-id="element-974">{language === 'en' ? 'Settings' : 'সেটিংস'}</span>
        </button>
      </div>

      {/* Settings Panel */}
      {showSettings && <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-975">
          <h3 className="font-semibold text-gray-900 mb-4" data-id="element-976">
            Notification Preferences
          </h3>
          <div className="space-y-4" data-id="element-977">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-978">
              <div className="p-4 bg-gray-50 rounded-lg" data-id="element-979">
                <div className="flex items-center gap-2 mb-3" data-id="element-980">
                  <AlertTriangleIcon className="w-5 h-5 text-red-600" data-id="element-981" />
                  <span className="font-medium text-gray-900" data-id="element-982">Khajna Due</span>
                </div>
                <div className="space-y-2" data-id="element-983">
                  <label className="flex items-center gap-2 text-sm" data-id="element-984">
                    <input type="checkbox" checked={settings.inAppKhajna} onChange={e => setSettings({
                  ...settings,
                  inAppKhajna: e.target.checked
                })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-985" />
                    <BellRingIcon className="w-4 h-4 text-gray-500" data-id="element-986" />
                    In-app
                  </label>
                  <label className="flex items-center gap-2 text-sm" data-id="element-987">
                    <input type="checkbox" checked={settings.emailKhajna} onChange={e => setSettings({
                  ...settings,
                  emailKhajna: e.target.checked
                })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-988" />
                    <MailIcon className="w-4 h-4 text-gray-500" data-id="element-989" />
                    Email
                  </label>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg" data-id="element-990">
                <div className="flex items-center gap-2 mb-3" data-id="element-991">
                  <FileTextIcon className="w-5 h-5 text-purple-600" data-id="element-992" />
                  <span className="font-medium text-gray-900" data-id="element-993">
                    Mutation Follow-up
                  </span>
                </div>
                <div className="space-y-2" data-id="element-994">
                  <label className="flex items-center gap-2 text-sm" data-id="element-995">
                    <input type="checkbox" checked={settings.inAppMutation} onChange={e => setSettings({
                  ...settings,
                  inAppMutation: e.target.checked
                })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-996" />
                    <BellRingIcon className="w-4 h-4 text-gray-500" data-id="element-997" />
                    In-app
                  </label>
                  <label className="flex items-center gap-2 text-sm" data-id="element-998">
                    <input type="checkbox" checked={settings.emailMutation} onChange={e => setSettings({
                  ...settings,
                  emailMutation: e.target.checked
                })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-999" />
                    <MailIcon className="w-4 h-4 text-gray-500" data-id="element-1000" />
                    Email
                  </label>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg" data-id="element-1001">
                <div className="flex items-center gap-2 mb-3" data-id="element-1002">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-1003" />
                  <span className="font-medium text-gray-900" data-id="element-1004">
                    Verification Status
                  </span>
                </div>
                <div className="space-y-2" data-id="element-1005">
                  <label className="flex items-center gap-2 text-sm" data-id="element-1006">
                    <input type="checkbox" checked={settings.inAppVerification} onChange={e => setSettings({
                  ...settings,
                  inAppVerification: e.target.checked
                })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1007" />
                    <BellRingIcon className="w-4 h-4 text-gray-500" data-id="element-1008" />
                    In-app
                  </label>
                  <label className="flex items-center gap-2 text-sm" data-id="element-1009">
                    <input type="checkbox" checked={settings.emailVerification} onChange={e => setSettings({
                  ...settings,
                  emailVerification: e.target.checked
                })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1010" />
                    <MailIcon className="w-4 h-4 text-gray-500" data-id="element-1011" />
                    Email
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-1012">
        <div className="border-b border-gray-200" data-id="element-1013">
          <div className="flex overflow-x-auto" data-id="element-1014">
            <button onClick={() => setActiveTab('all')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'all' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1015">
              All ({notifications.length})
            </button>
            <button onClick={() => setActiveTab('khajna')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'khajna' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1016">
              Khajna Due (
              {notifications.filter(n => n.type === 'khajna-due').length})
            </button>
            <button onClick={() => setActiveTab('mutation')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'mutation' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1017">
              Mutation (
              {notifications.filter(n => n.type === 'mutation-followup').length}
              )
            </button>
            <button onClick={() => setActiveTab('verification')} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'verification' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-1018">
              Verification (
              {notifications.filter(n => n.type === 'verification-changed').length}
              )
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="divide-y divide-gray-100" data-id="element-1019">
          {filteredNotifications.map(notification => <div key={notification.id} className={`p-4 hover:bg-gray-50 transition-colors ${!notification.read ? 'bg-blue-50/50' : ''}`} data-id="element-1020">
              <div className="flex items-start gap-4" data-id="element-1021">
                <div className={`p-2 rounded-lg ${notification.type === 'khajna-due' ? 'bg-red-100' : notification.type === 'mutation-followup' ? 'bg-purple-100' : 'bg-green-100'}`} data-id="element-1022">
                  {getIcon(notification.type)}
                </div>
                <div className="flex-1 min-w-0" data-id="element-1023">
                  <div className="flex items-start justify-between gap-4" data-id="element-1024">
                    <div data-id="element-1025">
                      <p className={`font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-1026">
                        {notification.message}
                      </p>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-1027">
                        {notification.landId} • {notification.landName}
                      </p>
                    </div>
                    {getUrgencyBadge(notification.urgency)}
                  </div>
                  <div className="flex items-center justify-between mt-3" data-id="element-1028">
                    <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-1029">
                      {notification.dueDate && <span className="flex items-center gap-1" data-id="element-1030">
                          <CalendarIcon className="w-4 h-4" data-id="element-1031" />
                          Due: {notification.dueDate}
                        </span>}
                      <span data-id="element-1032">{notification.createdAt}</span>
                    </div>
                    <button onClick={() => handleOpenRecord(notification)} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-1033">
                      Open Record
                      <ArrowRightIcon className="w-4 h-4" data-id="element-1034" />
                    </button>
                  </div>
                </div>
              </div>
            </div>)}
        </div>

        {filteredNotifications.length === 0 && <div className="px-4 py-12 text-center" data-id="element-1035">
            <BellIcon className="w-12 h-12 mx-auto text-gray-300 mb-3" data-id="element-1036" />
            <p data-id="element-1037" />
          </div>}
      </div>
    </div>;
}